import React, { useEffect, useState } from 'react';
import { councilPortal } from '../services/api';

const STATUS_COLORS = {
  scheduled:  'bg-blue-100 text-blue-800',
  completed:  'bg-green-100 text-green-800',
  cancelled:  'bg-gray-100 text-gray-600',
};

const TYPE_LABELS = {
  agm:     'AGM',
  regular: 'Regular',
  special: 'Special',
};

export default function CouncilMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    councilPortal.meetings()
      .then((res) => setMeetings(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  const parseMinutes = (raw) => {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Meetings & Minutes</h1>
        <p className="text-gray-500 text-sm mt-1">Read-only view of all strata meetings.</p>
      </div>

      {meetings.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          No meetings found.
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => {
            const minutes = parseMinutes(m.minutes);
            const isOpen = expanded === m.id;
            return (
              <div key={m.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setExpanded(isOpen ? null : m.id)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{m.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[m.status] || 'bg-gray-100 text-gray-600'}`}>
                          {m.status}
                        </span>
                        {m.meeting_type && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                            {TYPE_LABELS[m.meeting_type] || m.meeting_type}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {m.scheduled_date ? new Date(m.scheduled_date).toLocaleDateString('en-CA', {
                          weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                        }) : 'Date TBD'}
                        {m.location && ` · ${m.location}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {m.has_minutes && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                        Minutes ready
                      </span>
                    )}
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 px-6 py-5 bg-gray-50 space-y-5">
                    {m.agenda && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Agenda</h3>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.agenda}</p>
                      </div>
                    )}

                    {minutes ? (
                      <>
                        {minutes.summary && (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary</h3>
                            <p className="text-sm text-gray-700">{minutes.summary}</p>
                          </div>
                        )}

                        {minutes.sections?.length > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Discussion</h3>
                            <div className="space-y-3">
                              {minutes.sections.map((s, i) => (
                                <div key={i}>
                                  <p className="text-sm font-semibold text-gray-800">{s.title}</p>
                                  <p className="text-sm text-gray-600 mt-0.5">{s.content}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {minutes.decisions?.length > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Decisions</h3>
                            <ul className="space-y-1">
                              {minutes.decisions.map((d, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                  <span className="text-emerald-600 mt-0.5 shrink-0">✓</span>
                                  {d}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {minutes.action_items?.length > 0 && (
                          <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Action Items</h3>
                            <ul className="space-y-1.5">
                              {minutes.action_items.map((a, i) => {
                                const task       = typeof a === 'object' ? a.task        : a;
                                const assignedTo = typeof a === 'object' ? a.assigned_to : null;
                                const dueDate    = typeof a === 'object' ? a.due_date    : null;
                                return (
                                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                    <span className="text-blue-600 mt-0.5 shrink-0">→</span>
                                    <span>
                                      {task}
                                      {(assignedTo || dueDate) && (
                                        <span className="text-xs text-gray-400 ml-1">
                                          {assignedTo && `· ${assignedTo}`}{dueDate && ` · due ${dueDate}`}
                                        </span>
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : (
                      m.status === 'completed' && !m.has_minutes ? (
                        <p className="text-sm text-gray-400 italic">Minutes not yet available.</p>
                      ) : null
                    )}

                    {m.attendees?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Attendees</h3>
                        <p className="text-sm text-gray-600">{m.attendees.join(', ')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
